export const DEFAULT_THEME_COLOR = '#5371EA'
export const DEFAULT_THEME_REQUEST = '__DEFAULT_THEME__'
export const TENANT_FAVICON_STORAGE_PREFIX = 'dino_tenant_favicon:'

export function getTenantThemeRequestKey(authInfo, logoUrl) {
  const tenantCode = authInfo && authInfo.tenantCode
  if (typeof tenantCode !== 'string' || !tenantCode.trim()) return null
  return `${tenantCode.trim()}::${logoUrl || DEFAULT_THEME_REQUEST}`
}

export function parseTenantThemeRequestKey(requestKey) {
  if (typeof requestKey !== 'string') return null
  const separatorIndex = requestKey.indexOf('::')
  if (separatorIndex <= 0) return null
  const tenantCode = requestKey.slice(0, separatorIndex)
  const logoRequest = requestKey.slice(separatorIndex + 2)
  return {
    tenantCode,
    logoUrl: logoRequest === DEFAULT_THEME_REQUEST ? null : logoRequest
  }
}

export function normalizeFaviconUrl(logoUrl) {
  if (typeof logoUrl !== 'string') return null
  const value = logoUrl.trim()
  if (!value || /[\u0000-\u001F\u007F\\]/.test(value)) return null
  if (/^http:\/\//i.test(value)) return value.replace(/^http:/i, '')
  if (/^(?:https:)?\/\//i.test(value)) return value
  if (/^[a-z][a-z\d+.-]*:/i.test(value) || value.startsWith('#')) return null
  return value
}

export function getTenantFaviconStorageKey(tenantCode) {
  if (typeof tenantCode !== 'string') return null
  const value = tenantCode.trim()
  return /^[A-Za-z0-9_-]{1,64}$/.test(value)
    ? `${TENANT_FAVICON_STORAGE_PREFIX}${value}`
    : null
}

export function applyTenantFavicon(
  logoUrl,
  documentRef,
  tenantCode,
  storageRef
) {
  const targetDocument = documentRef ||
    (typeof document !== 'undefined' ? document : null)
  if (!targetDocument || !targetDocument.head) return null

  let link = targetDocument.querySelector('link[rel~="icon"]')
  if (!link) {
    link = targetDocument.createElement('link')
    link.rel = 'icon'
    targetDocument.head.appendChild(link)
  }

  const defaultAttribute = 'data-default-favicon'
  if (!link.hasAttribute(defaultAttribute)) {
    link.setAttribute(defaultAttribute, link.getAttribute('href') || '')
  }
  const defaultFavicon = link.getAttribute(defaultAttribute)
  const faviconUrl = normalizeFaviconUrl(logoUrl)
  const nextFavicon = faviconUrl || defaultFavicon
  const storageKey = getTenantFaviconStorageKey(tenantCode)
  let storage = storageRef
  if (storageRef === undefined) {
    try {
      storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null
    } catch (error) {
      storage = null
    }
  }

  if (storageKey && storage) {
    try {
      if (faviconUrl) {
        storage.setItem(storageKey, faviconUrl)
      } else {
        storage.removeItem(storageKey)
      }
    } catch (error) {
      // 浏览器禁用存储时仍可更新当前页面图标。
    }
  }

  link.onerror = null
  if (nextFavicon) {
    if (link.getAttribute('href') !== nextFavicon) {
      link.setAttribute('href', nextFavicon)
    }
  } else {
    link.removeAttribute('href')
  }

  if (faviconUrl) {
    link.onerror = () => {
      if (link.getAttribute('href') !== faviconUrl) return
      link.onerror = null
      if (storageKey && storage) {
        try {
          storage.removeItem(storageKey)
        } catch (error) {
          // 忽略不可用的浏览器存储。
        }
      }
      if (defaultFavicon) {
        link.setAttribute('href', defaultFavicon)
      } else {
        link.removeAttribute('href')
      }
    }
  }
  return nextFavicon || null
}

export function normalizeThemeColor(color) {
  if (typeof color !== 'string') return null
  const value = color.trim()
  return /^#[0-9a-fA-F]{6}$/.test(value) ? value.toUpperCase() : null
}

function rgbToHex(red, green, blue) {
  return `#${[red, green, blue]
    .map(value => Math.round(value).toString(16).padStart(2, '0'))
    .join('')}`.toUpperCase()
}

function hexToRgb(color) {
  const value = normalizeThemeColor(color)
  if (!value) return null
  return [
    parseInt(value.slice(1, 3), 16),
    parseInt(value.slice(3, 5), 16),
    parseInt(value.slice(5, 7), 16)
  ]
}

function rgbToHsl(red, green, blue) {
  const r = red / 255
  const g = green / 255
  const b = blue / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const lightness = (max + min) / 2
  if (max === min) return [0, 0, lightness]
  const delta = max - min
  const saturation = lightness > 0.5
    ? delta / (2 - max - min)
    : delta / (max + min)
  let hue
  if (max === r) hue = (g - b) / delta + (g < b ? 6 : 0)
  if (max === g) hue = (b - r) / delta + 2
  if (max === b) hue = (r - g) / delta + 4
  return [hue / 6, saturation, lightness]
}

function hueToRgb(p, q, hue) {
  let value = hue
  if (value < 0) value += 1
  if (value > 1) value -= 1
  if (value < 1 / 6) return p + (q - p) * 6 * value
  if (value < 1 / 2) return q
  if (value < 2 / 3) return p + (q - p) * (2 / 3 - value) * 6
  return p
}

function hslToHex(hue, saturation, lightness) {
  if (saturation === 0) {
    const gray = lightness * 255
    return rgbToHex(gray, gray, gray)
  }
  const q = lightness < 0.5
    ? lightness * (1 + saturation)
    : lightness + saturation - lightness * saturation
  const p = 2 * lightness - q
  return rgbToHex(
    hueToRgb(p, q, hue + 1 / 3) * 255,
    hueToRgb(p, q, hue) * 255,
    hueToRgb(p, q, hue - 1 / 3) * 255
  )
}

function normalizeHeaderColor(color) {
  const rgb = hexToRgb(color)
  if (!rgb) return null
  const [hue, saturation, lightness] = rgbToHsl(...rgb)
  const safeLightness = Math.max(0.08, Math.min(0.45, lightness - 0.1))
  return hslToHex(hue, saturation, safeLightness)
}

export function createActiveThemeColor(color) {
  const rgb = hexToRgb(color || DEFAULT_THEME_COLOR)
  if (!rgb) return createActiveThemeColor(DEFAULT_THEME_COLOR)
  const [hue, saturation, lightness] = rgbToHsl(...rgb)
  return hslToHex(hue, saturation, Math.max(0.2, lightness - 0.14))
}

function mixThemeColorWithWhite(color, whiteWeight) {
  const rgb = hexToRgb(color || DEFAULT_THEME_COLOR)
  if (!rgb) return mixThemeColorWithWhite(DEFAULT_THEME_COLOR, whiteWeight)
  return rgbToHex(...rgb.map(channel => (
    channel * (1 - whiteWeight) + 255 * whiteWeight
  )))
}

export function extractDominantColorFromPixels(pixels) {
  if (!pixels || pixels.length < 4) return null
  const buckets = {}
  for (let index = 0; index + 3 < pixels.length; index += 4) {
    const red = pixels[index]
    const green = pixels[index + 1]
    const blue = pixels[index + 2]
    const alpha = pixels[index + 3]
    if (alpha < 128) continue
    const max = Math.max(red, green, blue)
    const min = Math.min(red, green, blue)
    const lightness = (max + min) / 510
    const saturation = max === 0 ? 0 : (max - min) / max
    if (lightness < 0.08 || lightness > 0.92 || saturation < 0.12) continue
    const key = `${red >> 5}-${green >> 5}-${blue >> 5}`
    const bucket = buckets[key] || { red: 0, green: 0, blue: 0, weight: 0 }
    const weight = 1 + saturation * 3
    bucket.red += red * weight
    bucket.green += green * weight
    bucket.blue += blue * weight
    bucket.weight += weight
    buckets[key] = bucket
  }
  const dominant = Object.values(buckets)
    .sort((left, right) => right.weight - left.weight)[0]
  if (!dominant) return null
  return normalizeHeaderColor(rgbToHex(
    dominant.red / dominant.weight,
    dominant.green / dominant.weight,
    dominant.blue / dominant.weight
  ))
}

export function extractDominantColor(imageSource) {
  if (!imageSource || typeof Image === 'undefined') return Promise.resolve(null)
  return new Promise(resolve => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = 48
        canvas.height = 48
        const context = canvas.getContext('2d')
        context.drawImage(image, 0, 0, canvas.width, canvas.height)
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data
        resolve(extractDominantColorFromPixels(pixels))
      } catch (error) {
        resolve(null)
      }
    }
    image.onerror = () => resolve(null)
    image.src = imageSource
  })
}

export function getThemeColorEndpoint(logoUrl) {
  if (typeof logoUrl !== 'string' || !/\/logo(?:\?|$)/.test(logoUrl)) return null
  return logoUrl.replace(/\/logo(?=\?|$)/, '/theme-color')
}

export async function loadTenantThemeColor(logoUrl, fetcher, tenantCode) {
  const endpoint = getThemeColorEndpoint(logoUrl)
  const request = fetcher || (typeof fetch === 'function' ? fetch.bind(window) : null)
  if (endpoint && request) {
    try {
      const options = { credentials: 'include' }
      if (tenantCode) {
        options.headers = { 'X-Dino-Tenant-Code': tenantCode }
      }
      const response = await request(endpoint, options)
      if (response.ok === false) throw new Error('theme color request failed')
      const payload = await response.json()
      const configuredColor = normalizeThemeColor(payload && payload.data)
      if (configuredColor) return configuredColor
    } catch (error) {
      // 未配置或读取失败时保持系统原主题色。
    }
  }
  return DEFAULT_THEME_COLOR
}

export function createThemeStyle(color) {
  const themeColor = normalizeThemeColor(color) || DEFAULT_THEME_COLOR
  return {
    '--tenant-top-nav-height': '60px',
    '--tenant-theme-color': themeColor,
    '--tenant-theme-active-color': createActiveThemeColor(themeColor),
    '--tenant-theme-disabled-color': mixThemeColorWithWhite(themeColor, 0.55),
    '--tenant-theme-soft-color': mixThemeColorWithWhite(themeColor, 0.9)
  }
}

export function applyTenantThemeToDocument(color) {
  const themeStyle = createThemeStyle(color)
  if (typeof document !== 'undefined') {
    Object.keys(themeStyle).forEach(name => {
      document.documentElement.style.setProperty(name, themeStyle[name])
    })
  }
  return themeStyle
}
